<script lang="ts">
    import { DEFAULT_LAYOUT } from '$lib/components/constants';

    import { page } from '$app/stores';
    import { getLinkingAriaLabel } from '$lib/components/Blocks/_utils/getLinkingAriaLabel';
    import { getMediaById } from '$lib/components/Blocks/_utils/getMediaById';
    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { NextPageNavigator } from '$lib/global/helper/NextPageNavigator';
    import { getShadow } from '$lib/global/helper/shadow';
    import { trackImageClick } from '$lib/global/tracking';
    import { cn } from '$lib/utils/cn';

    import get from 'lodash/get';

    import ImageView from './Image.View.svelte';
    import { getWidthByScreenSize } from '../_utils/getWidthByScreenSize';

    import type { ImageContent } from '$lib/types/Components/Image/ImageContent';
    import type { Language } from '$lib/types/Language';
    import type { BrandShadow, Theme, TransitionEffect } from '$lib/types/Theme';

    export let id = '';
    export let content: ImageContent;
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'none';
    export let layout = DEFAULT_LAYOUT;
    export let nextPage = '';
    export let firstResultPage = '';
    export let campaignId = '';
    export let theme: Theme | undefined = undefined;
    export let language: Language = 'en';

    const {
        data: { pageIdSlugMap },
    } = $page;

    const ariaLabel = content.linkingEnabled ? getLinkingAriaLabel(content.linking, language) : '';

    const regularNextPage: string = get(content, 'linking.pageId', nextPage);
    const externalLink: string = get(content, 'linking.url', '');
    const nextPageNavigator = new NextPageNavigator(campaignId, firstResultPage, pageIdSlugMap);

    const borderRadius = getBorderRadius('image');
    const isFramedOrInFramedColumn = content.framed || layout.isInFramedColumn;
    const shadowClass =
        content.framed && !layout.isInColumn && theme?.shadow
            ? getShadow('blockFrame', theme.shadow as BrandShadow)
            : undefined;

    // Get media from media store
    const media = getMediaById(
        content.mediaIds && content.mediaIds.length > 0 ? content.mediaIds[0] : '',
    );

    const handleClick = () => {
        if (!content.linkingEnabled || !content.linking || !content.clickId) {
            return;
        }

        // Tracking
        trackImageClick(id, content.clickId);

        // Navigate to external page
        if (!content.linking.isInternal && externalLink) {
            return nextPageNavigator.goExternal(externalLink);
        }

        return nextPageNavigator.go(regularNextPage);
    };
</script>

<ImageView
    {id}
    src={content.src}
    framed={content.framed}
    size={content.size}
    box={content.box}
    fixedMaxWidth={getWidthByScreenSize()}
    borderRadiusClass={cn(borderRadius, {
        'max-sm:fun-rounded-none': !isFramedOrInFramedColumn,
    })}
    {shadowClass}
    onClick={content.linkingEnabled && content.linking && content.clickId ? handleClick : undefined}
    alt={media?.alt || media?.altDescription}
    {ariaLabel}
    {transitionDelay}
    {transitionEffect}
    {layout}
/>
